import { useEffect } from "react";
// Third Party
import { useQuery, useQueryClient } from "@tanstack/react-query";

// API
import { ADMINISTRATION_TOOLS_SUBSCRIPTION, getAdministrationTools } from "@Settings/api/AdministrationTools";
import { getSystemSettings } from "@Settings/api/SystemSettings/queries";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Types
import { IAdministrationTools } from "@Settings/types/administrationTools/model";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";

/**
 * Hook to get the configuration data
 * @param locationId
 */
function useConfigurationData({ locationId }: { locationId: string | null }) {
	const queryClient = useQueryClient();
	const { addSubscription, deleteSubscription, getSubscription } = useSubscriptions();

	const {
		data: configuration,
		error,
		isLoading,
	} = useQuery({
		queryKey: ["settingsAndConfigurations", locationId],
		queryFn: async () => {
			const administrationTools = await getAdministrationTools(locationId as string);
			const systemSettings = await getSystemSettings(locationId as string);
			return {
				administrationTools,
				systemSettings,
			};
		},
		enabled: Boolean(locationId),
		staleTime: Infinity,
	});

	// Configuration subscription
	const configurationSubscription = getSubscription(["configurationSubscription", locationId]);
	configurationSubscription?.subscriptions?.onUpdateAdministrationTools?.setCallback(
		(response: GraphqlSubscriptionResponse<"onUpdateAdministrationTools", IAdministrationTools>) => {
			const updatedAdministrationTools = response.data?.onUpdateAdministrationTools;
			queryClient.setQueryData(["settingsAndConfigurations", locationId], { administrationTools: updatedAdministrationTools });
		}
	);

	useEffect(() => {
		if (locationId) {
			// Add subscription
			addSubscription(
				["configurationSubscription", locationId],
				[
					{
						context: "onUpdateAdministrationTools",
						query: ADMINISTRATION_TOOLS_SUBSCRIPTION,
						variables: {
							locationId,
						},
					},
				]
			);
		}

		return () => {
			// Clean up subscription
			deleteSubscription(["configurationSubscription", locationId]);
		};
	}, [locationId]);

	return { configuration, error, isLoading };
}

export default useConfigurationData;
